<template>
  <v-dialog
    v-model="internalValue"
    max-width="650"
    scrollable
    :persistent="loading"
  >
    <v-card data-testid="associations-modal" :data-entity-id="entity && entity.id" class="rounded-lg">
      <!-- Modal Header: Entity identity without avatar icons -->
      <v-card-title class="d-flex justify-space-between align-center pa-4">
        <div>
          <div class="text-h6 font-weight-bold">{{ entity ? entity.name : '' }}</div>
          <div class="caption grey--text">
            Associated Banks | {{ typeLabel }}
            <span v-if="entity && entity.cnpj"> | CNPJ: {{ entity.cnpj }}</span>
          </div>
        </div>
        <v-btn icon @click="close" :disabled="loading">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-progress-linear v-if="loading" indeterminate color="primary" height="2" />
      <v-divider v-else />

      <v-card-text class="pa-4" style="max-height: 540px;">
        <!-- Link Bank Section (natural card background) -->
        <v-card outlined class="pa-3 mb-4 rounded-lg">
          <div class="subtitle-2 font-weight-bold mb-2">Link Bank</div>
          <div class="d-flex align-center flex-gap-2">
            <v-select
              data-testid="add-association"
              v-model="selectedBankId"
              :items="availableBankOptions"
              item-text="displayName"
              item-value="id"
              :item-disabled="(item) => item.disabled"
              label="Select bank to link"
              outlined
              dense
              hide-details
              :disabled="loading"
              class="flex-grow-1 select-bank-dropdown"
            >
              <template v-slot:item="{ item }">
                <div data-testid="association-option" :data-entity-id="item.id" class="d-flex flex-column py-1 w-100 dropdown-item-text">
                  <div class="d-flex align-center">
                    <span
                      :class="{ 'grey--text': item.disabled }"
                      class="font-weight-medium"
                      style="font-size: 1.05rem;"
                    >
                      {{ item.name }}
                    </span>
                    <span v-if="item.bankcode" class="caption grey--text ml-2" style="font-size: 0.85rem;">
                      ({{ item.bankcode }})
                    </span>
                  </div>
                  <div
                    v-if="item.disabledReason"
                    class="caption grey--text text--darken-1 mt-1 pl-1"
                    style="font-size: 0.85rem;"
                  >
                    {{ item.disabledReason }}
                  </div>
                  <div
                    v-else-if="item.cnpj"
                    class="caption grey--text mt-1 pl-1"
                    style="font-size: 0.85rem;"
                  >
                    CNPJ: {{ item.cnpj }}
                  </div>
                </div>
              </template>

              <!-- Empty dropdown fallback -->
              <template v-slot:no-data>
                <div class="pa-3 text-caption grey--text text-center" style="font-size: 0.95rem;">
                  All banks are already associated with this {{ typeLabel.toLowerCase() }}
                </div>
              </template>
            </v-select>

            <v-btn
              data-testid="link-association"
              color="primary"
              :disabled="!selectedBankId || isSelectedDisabled || loading"
              :loading="loadingAction === 'add'"
              @click="handleAddBank"
            >
              <v-icon left small>mdi-plus</v-icon>
              Link
            </v-btn>
          </div>

          <!-- Muted guidance when all available banks require master broker link -->
          <div
            v-if="hasParentBroker && allBanksDisabled"
            class="caption grey--text mt-2"
          >
            To link a bank, its Broker ({{ parentBroker.name }}) must be linked to that bank first.
          </div>
        </v-card>

        <!-- Associated Banks List -->
        <div>
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="subtitle-2 font-weight-bold">
              Associated Banks ({{ linkedBanks.length }})
            </span>
          </div>

          <!-- Empty State -->
          <div
            v-if="!linkedBanks.length"
            class="d-flex flex-column align-center justify-center pa-6 text-center grey--text"
          >
            <v-icon size="40" color="grey darken-1" class="mb-2">mdi-bank-outline</v-icon>
            <div class="text-body-2 font-weight-medium">
              No banks associated with this {{ typeLabel.toLowerCase() }} yet
            </div>
            <div class="caption">Use the dropdown above to link a bank.</div>
          </div>

          <!-- List of Linked Banks -->
          <v-list v-else dense class="pa-0">
            <v-card
              v-for="bank in linkedBanks"
              data-testid="associated-bank"
              :data-entity-id="bank.id"
              :key="bank.id"
              outlined
              class="mb-2 pa-3 rounded-lg"
            >
              <div class="d-flex align-center justify-space-between">
                <div class="d-flex flex-column">
                  <div class="d-flex align-center flex-gap-2">
                    <v-chip
                      v-if="type === 'broker' && bank.is_override"
                      x-small
                      outlined
                      color="primary"
                      class="mr-2"
                    >
                      {{ bank.is_override }}
                    </v-chip>
                    <span class="font-weight-bold text-body-1">{{ bank.name }}</span>
                  </div>

                  <div class="caption grey--text mt-1">
                    <span v-if="bank.bankcode">Code: {{ bank.bankcode }} | </span>
                    <span>CNPJ: {{ bank.cnpj || '-' }}</span>
                    <span v-if="bank.email && bank.email !== '-'"> | {{ bank.email }}</span>
                  </div>
                </div>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      v-bind="attrs"
                      v-on="on"
                      :disabled="loading"
                      data-testid="remove-association"
                      @click="requestRemoveBank(bank)"
                    >
                      <v-icon small color="error">mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Unlink bank</span>
                </v-tooltip>
              </div>
            </v-card>
          </v-list>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-3 justify-end">
        <v-btn data-testid="close-associations" text @click="close" :disabled="loading">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Confirmation Modal for Unlinking a Bank -->
    <delete-modal
      v-model="confirmDeleteDialog"
      :loading="loadingAction === 'remove'"
      :item-label="pendingRemoveBank ? pendingRemoveBank.name : ''"
      title="Unlink Bank"
      message="Are you sure you want to unlink this bank?"
      confirm-text="Unlink"
      @confirm="confirmRemoveBank"
    >
      <!-- Cascade Warning for Master Brokers -->
      <div
        v-if="dependentPartners.length || dependentCompanies.length"
        class="mt-2 pa-2 rounded orange darken-4 text-caption white--text"
      >
        <div class="font-weight-bold mb-1">Cascade Warning:</div>
        <div>
          This is a Broker with dependent partners and clients. Unlinking this bank will automatically
          remove it from:
        </div>
        <ul v-if="dependentPartners.length" class="mt-1 pl-4">
          <li v-for="p in dependentPartners" :key="p.id">
            {{ p.name }} (Partner)
          </li>
        </ul>
        <ul v-if="dependentCompanies.length" class="mt-1 pl-4">
          <li v-for="c in dependentCompanies" :key="c.id">
            {{ c.name }} (Company)
          </li>
        </ul>
      </div>
    </delete-modal>
  </v-dialog>
</template>

<script>
import DeleteModal from '@/components/modal/DeleteModal.vue'
import convertToCNPJ from '@/utils'

export default {
  name: 'EntityBanksModal',
  components: { DeleteModal },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    entity: {
      type: Object,
      default: null
    },
    type: {
      // 'broker' | 'company'
      type: String,
      required: true
    },
    availableBanks: {
      type: Array,
      default: () => []
    },
    bankList: {
      type: Array,
      default: () => []
    },
    brokerList: {
      type: Array,
      default: () => []
    },
    companyList: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedBankId: null,
      loadingAction: null,
      confirmDeleteDialog: false,
      pendingRemoveBank: null
    }
  },
  computed: {
    internalValue: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    typeLabel() {
      if (this.type === 'broker') {
        return this.isPartnerBroker ? 'Partner' : 'Broker'
      }
      return 'Company'
    },
    // Set of bank IDs currently linked to this entity
    linkedBankIdSet() {
      if (!this.entity || !Array.isArray(this.entity.banklist)) return new Set()
      return new Set(this.entity.banklist.filter(b => b && typeof b === 'object').map(b => b.id))
    },
    // Whether this broker is a partner (child of a master broker)
    isPartnerBroker() {
      if (this.type !== 'broker' || !this.entity) return false
      const fullBroker = this.brokerList.find(b => b.id === this.entity.id) || this.entity
      const partmasterId = fullBroker.partmaster_id
      return !!(partmasterId && partmasterId !== 0 && partmasterId !== '0')
    },
    // Parent broker for partner brokers or client companies
    parentBroker() {
      if (!this.entity) return null
      if (this.isPartnerBroker) {
        const fullBroker = this.brokerList.find(b => b.id === this.entity.id) || this.entity
        return this.brokerList.find(b => b.id === fullBroker.partmaster_id) || null
      }
      if (this.type === 'company') {
        const brokerId = this.entity.parent_broker_id || this.entity.broker
        return this.brokerList.find(b => b.id === brokerId) || null
      }
      return null
    },
    hasParentBroker() {
      return !!this.parentBroker
    },
    // Enriched list of currently linked banks
    linkedBanks() {
      if (!this.entity || !Array.isArray(this.entity.banklist)) return []
      return this.entity.banklist.map(b => {
        const fullBank = this.bankList.find(bank => bank.id === b.id)
        return {
          id: b.id,
          name: b.name || fullBank?.name || b.id,
          bankcode: fullBank?.bankcode || '',
          cnpj: convertToCNPJ(fullBank?.cnpj || b.cnpj || ''),
          email: fullBank?.email || b.email || '',
          is_override: b.is_override ?? fullBank?.is_override ?? null
        }
      }).sort((a, b) => a.name.localeCompare(b.name))
    },
    // Available banks with hierarchy constraints for partner brokers and companies
    availableBankOptions() {
      if (!this.entity) return []
      const parent = this.parentBroker
      // Only {id, ...} entries are links. The API does not count a bare bank
      // id as one, so neither can this.
      const parentBankIds = parent && Array.isArray(parent.banklist)
        ? new Set(parent.banklist.filter(b => b && typeof b === 'object').map(b => b.id))
        : null

      return (this.bankList || [])
        .filter(bank => !this.linkedBankIdSet.has(bank.id))
        .map(bank => {
          let disabled = false
          let disabledReason = ''

          if (this.isPartnerBroker) {
            const isMasterLinked = parentBankIds ? parentBankIds.has(bank.id) : false
            if (!isMasterLinked) {
              disabled = true
              disabledReason = `Broker (${parent?.name || 'Broker'}) must be linked first`
            }
          } else if (this.type === 'company') {
            if (!parent) {
              disabled = true
              disabledReason = 'No broker assigned to this company'
            } else {
              const isParentLinked = parentBankIds ? parentBankIds.has(bank.id) : false
              if (!isParentLinked) {
                disabled = true
                disabledReason = `Broker (${parent.name || 'Broker'}) must be linked first`
              }
            }
          }

          return {
            id: bank.id,
            name: bank.name,
            displayName: bank.name,
            bankcode: bank.bankcode,
            cnpj: convertToCNPJ(bank.cnpj || ''),
            disabled,
            disabledReason
          }
        })
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    isSelectedDisabled() {
      const selected = this.availableBankOptions.find(o => o.id === this.selectedBankId)
      return selected ? selected.disabled : false
    },
    allBanksDisabled() {
      return this.availableBankOptions.length > 0 && this.availableBankOptions.every(b => b.disabled)
    },
    // Dependent partners that use this bank through this master broker
    dependentPartners() {
      if (!this.entity || this.type !== 'broker' || this.isPartnerBroker || !this.pendingRemoveBank) {
        return []
      }
      const bankId = this.pendingRemoveBank.id
      return this.brokerList.filter(
        b => b.partmaster_id === this.entity.id && Array.isArray(b.banklist) && b.banklist.some(bl => bl.id === bankId)
      )
    },
    // Dependent companies that use this bank under this master broker or its partners
    dependentCompanies() {
      if (!this.entity || this.type !== 'broker' || this.isPartnerBroker || !this.pendingRemoveBank) {
        return []
      }
      const bankId = this.pendingRemoveBank.id
      const partnerIds = new Set(this.dependentPartners.map(p => p.id))
      return this.companyList.filter(
        c => (c.broker === this.entity.id || partnerIds.has(c.broker)) &&
          Array.isArray(c.banklist) && c.banklist.some(bl => bl.id === bankId)
      )
    }
  },
  methods: {
    close() {
      if (!this.loading) {
        this.internalValue = false
        this.selectedBankId = null
      }
    },
    handleAddBank() {
      if (!this.selectedBankId || this.isSelectedDisabled) return
      this.loadingAction = 'add'
      this.$emit('add-bank', {
        entity: this.entity,
        type: this.type,
        bankId: this.selectedBankId,
        onComplete: () => {
          this.loadingAction = null
          this.selectedBankId = null
        }
      })
    },
    requestRemoveBank(bank) {
      this.pendingRemoveBank = bank
      this.confirmDeleteDialog = true
    },
    confirmRemoveBank() {
      if (!this.pendingRemoveBank) return
      this.loadingAction = 'remove'
      const bankId = this.pendingRemoveBank.id
      this.$emit('remove-bank', {
        entity: this.entity,
        type: this.type,
        bankId,
        onComplete: () => {
          this.loadingAction = null
          this.confirmDeleteDialog = false
          this.pendingRemoveBank = null
        }
      })
    }
  }
}
</script>

<style scoped>
::v-deep .v-select.select-bank-dropdown .v-select__selections,
::v-deep .v-select.select-bank-dropdown .v-select__selection {
  font-size: 1.05rem !important;
}
::v-deep .v-select.select-bank-dropdown .v-label {
  font-size: 1rem !important;
}
</style>
